import { webSocket, WebSocketSubject } from "rxjs/webSocket";
import { Subject, Subscription, Observable, map, merge, filter, tap, interval, takeWhile } from "rxjs";
import { Channel } from "./channel";
import {
  IncomingMessages,
  isIncomingMessage,
  OutgoingMessages,
  InChannelMessages,
  OutChannelBroadcast,
} from "./message";

export type ChannelTokenRefresh = () => Promise<string>;

export type PubSubClientOptions = {
  host: string;
  appId: string;
  // Called before each reconnect. The presigned host URL is signed with temporary SSO
  // credentials, so it goes stale long before its own X-Amz-Expires does.
  refreshUrlFn?: () => Promise<string>;
};

type JoinedChannel = {
  token?: string;
  refresh?: ChannelTokenRefresh;
};

const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30000;
// A tab hidden longer than this stops reconnecting: nobody is watching, and retrying
// forever churns connections. Becoming visible again is what resumes it.
const HIDDEN_RECONNECT_CUTOFF = 60 * 60 * 1000;

export class PubSubClient {
  private socket: WebSocketSubject<IncomingMessages | OutgoingMessages>;
  public readonly connect$: Observable<boolean>;
  public readonly close$: Observable<Event>;
  public readonly reconnected$ = new Subject<void>();
  protected readonly messages$ = new Subject<unknown>();
  private readonly channelInStream: Observable<InChannelMessages>;
  public readonly channelErrors$: Observable<IncomingMessages>;
  private open: boolean = false;
  private disposed: boolean = false;
  private reconnectAttempt: number = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private currentUrl: string;
  private hiddenSince: number | null = null;
  private pingSub: Subscription | null = null;
  private channeldBroadcastStream = new Subject<OutChannelBroadcast<unknown>>();
  private readonly joinedChannels = new Map<string, JoinedChannel>();
  private readonly connectSubject = new Subject<Event>();
  private readonly closeSubject = new Subject<Event>();

  constructor(private readonly options: PubSubClientOptions) {
    if (typeof window === "undefined") {
      throw new Error("This client is only supported in the browser");
    }

    this.currentUrl = options.host;
    document.addEventListener("visibilitychange", this.onVisibilityChange);

    this.socket = this.createSocket();

    this.channelErrors$ = this.messages$
      .asObservable()
      .pipe(
        filter(
          (m): m is IncomingMessages =>
            isIncomingMessage(m) && m.service === "channel" && m.action === "error",
        ),
      );

    this.channelInStream = this.messages$
      .asObservable()
      .pipe(filter((s): s is InChannelMessages => isIncomingMessage(s) && s.service === "channel"));

    this.connectSubject.subscribe(() => {
      const wasReconnect = this.reconnectAttempt > 0;
      this.open = true;
      this.reconnectAttempt = 0;
      this.pingSub?.unsubscribe();
      this.pingSub = interval(30000)
        .pipe(takeWhile(() => this.open && !this.disposed))
        .subscribe(() => {
          this.socket.next({ service: "root", action: "ping" });
        });
      if (wasReconnect) {
        this.rejoinChannels();
        this.reconnected$.next();
      }
    });

    this.closeSubject.subscribe(() => {
      this.open = false;
    });

    this.channeldBroadcastStream.subscribe({
      next: (m) => {
        if (m.action !== "broadcast") {
          console.warn("Illegal channel action received. Skipping send");
          return;
        }
        this.socket.next(m);
      },
    });
    this.subscribeToSocket();
    this.connect$ = merge(this.connectSubject, this.closeSubject).pipe(map((event) => event.type === "open"));
    this.close$ = this.closeSubject.pipe(tap(() => {}));
  }

  private createSocket(): WebSocketSubject<IncomingMessages | OutgoingMessages> {
    return webSocket({
      url: this.currentUrl,
      openObserver: this.connectSubject,
      closeObserver: this.closeSubject,
    });
  }

  private subscribeToSocket(): void {
    this.socket.subscribe({
      next: (message) => {
        this.messages$.next(message);
      },
      error: () => {
        this.scheduleReconnect();
      },
      complete: () => {
        if (!this.disposed) {
          this.scheduleReconnect();
        }
      },
    });
  }

  private scheduleReconnect(): void {
    if (this.disposed) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

    this.open = false;

    // Nobody is watching a long-hidden tab; onVisibilityChange resumes this when it returns.
    if (this.hiddenTooLong()) return;
    const jitter = Math.random() * RECONNECT_BASE_DELAY;
    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempt) + jitter,
      RECONNECT_MAX_DELAY,
    );

    this.reconnectTimer = setTimeout(() => void this.reconnectNow(), delay);
  }

  private async reconnectNow(): Promise<void> {
    if (this.disposed) return;
    if (this.options.refreshUrlFn) {
      try {
        this.currentUrl = await this.options.refreshUrlFn();
      } catch {
        // Keep the previous URL and let the backoff try again rather than giving up.
      }
    }
    if (this.disposed) return;
    this.reconnectAttempt++;
    this.socket = this.createSocket();
    this.subscribeToSocket();
  }

  private hiddenTooLong(): boolean {
    return this.hiddenSince !== null && Date.now() - this.hiddenSince >= HIDDEN_RECONNECT_CUTOFF;
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      this.hiddenSince ??= Date.now();
      return;
    }
    this.hiddenSince = null;
    if (this.disposed || this.open) return;
    // Back in view with the socket down: connect straight away. Going through
    // scheduleReconnect would still cost a base delay plus jitter for no reason.
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectAttempt = 0;
    void this.reconnectNow();
  };

  private rejoinChannels(): void {
    this.joinedChannels.forEach(async (entry, channel) => {
      let token = entry.token;
      if (entry.refresh) {
        try {
          token = await entry.refresh();
          this.joinedChannels.set(channel, { ...entry, token });
        } catch {
          // Fall back to the existing token: a stale one is still worth trying, and a
          // rejected join is louder than never sending one.
        }
      }
      this.socket.next({
        data: { channel },
        service: "channel",
        token,
        appId: this.options.appId,
        action: "join",
      });
    });
  }

  public join = (
    channel: string,
    token: string | undefined = undefined,
    refresh: ChannelTokenRefresh | undefined = undefined,
  ): Channel => {
    this.joinedChannels.set(channel, { token, refresh });

    this.socket.next({
      data: { channel },
      service: "channel",
      token: token,
      appId: this.options.appId,
      action: "join",
    });

    return new Channel(
      channel,
      this.options.appId,
      this.channelInStream.pipe(
        filter((m) => {
          return m.data.channel === channel;
        }),
      ),
      this.channeldBroadcastStream,
      this.leaveChannel(channel),
    );
  };

  private readonly leaveChannel = (name: string) => {
    return () => {
      this.joinedChannels.delete(name);
      this.socket.next({
        appId: this.options.appId,
        action: "leave",
        service: "channel",
        data: {
          channel: name,
        },
      });
    };
  };

  public disconnect = (): void => {
    this.disposed = true;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.socket.complete();
    this.messages$.complete();
    this.reconnected$.complete();
  };
}
